import React from 'react';

/**
 * TympanogramCanvas — SVG replacement for the previous <canvas>-based
 * renderer. Auto-plots a Gaussian tympanogram curve from
 * (jergerType, mePressure, compliance) and draws it inside a viewBox
 * so grid lines / curve / axis labels stay crisp at any zoom AND
 * through html2canvas → jsPDF export (canvas got double-rasterised at
 * PDF time producing the "blurred tympanogram" bug the clinic reported).
 *
 * Curve model — unchanged from the canvas version so visual output
 * matches 1:1 (aside from crispness):
 *   y(x) = C * exp(-((x - P)² / (2σ²)))
 * σ per Jerger type: A:60, As:45, Ad:55, B:1000 (flat), C:70.
 * probeHz ≥ 678 broadens σ by 1.25× (infant-like).
 *
 * Volume does NOT influence the curve — only P (peak x) and C (peak y).
 */

const VB = { w: 320, h: 200 };
const PAD = { top: 16, right: 10, bottom: 28, left: 36 };
const CW = VB.w - PAD.left - PAD.right;
const CH = VB.h - PAD.top - PAD.bottom;

const X_MIN = -400;
const X_MAX = 200;
const Y_MIN = 0;

const TYPE_SIGMA = { A: 60, As: 45, Ad: 55, B: 1000, C: 70 };

const TympanogramCanvas = ({
  jergerType,
  mePressure,
  compliance,
  earSide = 'right',
  probeHz = 226,
}) => {
  const C = typeof compliance === 'number' && !Number.isNaN(compliance) ? compliance : null;
  const P = typeof mePressure === 'number' && !Number.isNaN(mePressure) ? mePressure : null;
  const Y_MAX = Math.max(1.5, (C ?? 1.0) + 0.5);

  const xToPx = (daPa) => PAD.left + ((daPa - X_MIN) / (X_MAX - X_MIN)) * CW;
  const yToPx = (mL) => PAD.top + (1 - (mL - Y_MIN) / (Y_MAX - Y_MIN)) * CH;

  const color = earSide === 'right' ? '#DC2626' : '#2563EB';
  const sigmaBase = TYPE_SIGMA[jergerType] || 60;
  const probeBroaden = probeHz >= 678 ? 1.25 : 1;
  const sigma = sigmaBase * probeBroaden;

  // Vertical grid lines (every 100 daPa).
  const xGrid = [];
  for (let p = X_MIN; p <= X_MAX; p += 100) xGrid.push(p);

  // Horizontal grid lines (every 0.5 mL).
  const yGrid = [];
  for (let y = 0; y <= Y_MAX + 1e-9; y += 0.5) yGrid.push(+y.toFixed(2));

  // Curve path — only when we have both peak pressure AND compliance.
  let curvePath = '';
  if (P !== null && C !== null) {
    for (let x = X_MIN; x <= X_MAX; x += 2) {
      const y = C * Math.exp(-Math.pow(x - P, 2) / (2 * sigma * sigma));
      const px = xToPx(x);
      const py = yToPx(y);
      curvePath += (x === X_MIN ? 'M ' : 'L ') + px.toFixed(2) + ' ' + py.toFixed(2) + ' ';
    }
  }

  return (
    <svg
      viewBox={`0 0 ${VB.w} ${VB.h}`}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full bg-white"
      shapeRendering="geometricPrecision"
      style={{ imageRendering: 'crisp-edges', display: 'block' }}
      data-testid={`tympanogram-svg-${earSide}`}
      role="img"
      aria-label={`${earSide === 'right' ? 'Right' : 'Left'} tympanogram`}
    >
      {/* Grid — vertical (daPa) */}
      {xGrid.map((p) => {
        const x = xToPx(p);
        return (
          <line
            key={`vg-${p}`}
            x1={x} y1={PAD.top}
            x2={x} y2={PAD.top + CH}
            stroke="#e5e7eb"
            strokeWidth="0.5"
          />
        );
      })}
      {/* Grid — horizontal (mL) */}
      {yGrid.map((y) => {
        const yy = yToPx(y);
        return (
          <line
            key={`hg-${y}`}
            x1={PAD.left} y1={yy}
            x2={PAD.left + CW} y2={yy}
            stroke="#e5e7eb"
            strokeWidth="0.5"
          />
        );
      })}

      {/* Chart border */}
      <rect
        x={PAD.left} y={PAD.top}
        width={CW} height={CH}
        fill="none"
        stroke="#94a3b8"
        strokeWidth="1"
      />

      {/* X-axis labels (daPa) */}
      {xGrid.map((p) => (
        <text
          key={`xl-${p}`}
          x={xToPx(p)}
          y={PAD.top + CH + 10}
          fill="#475569"
          fontFamily="Arial, sans-serif"
          fontSize="9"
          textAnchor="middle"
        >
          {String(p)}
        </text>
      ))}
      {/* Y-axis labels (mL) */}
      {yGrid.map((y) => (
        <text
          key={`yl-${y}`}
          x={PAD.left - 4}
          y={yToPx(y) + 3}
          fill="#475569"
          fontFamily="Arial, sans-serif"
          fontSize="9"
          textAnchor="end"
        >
          {y.toFixed(1)}
        </text>
      ))}

      {/* Axis titles */}
      <text
        x={PAD.left + CW / 2}
        y={VB.h - 3}
        fill="#475569"
        fontFamily="Arial, sans-serif"
        fontSize="9"
        fontWeight="700"
        textAnchor="middle"
      >
        daPa
      </text>
      <text
        x={10}
        y={PAD.top + CH / 2}
        fill="#475569"
        fontFamily="Arial, sans-serif"
        fontSize="9"
        fontWeight="700"
        textAnchor="middle"
        transform={`rotate(-90 10 ${PAD.top + CH / 2})`}
      >
        mL
      </text>

      {P === null || C === null ? (
        <text
          x={PAD.left + CW / 2}
          y={PAD.top + CH / 2}
          fill="#9ca3af"
          fontFamily="Arial, sans-serif"
          fontSize="10"
          fontStyle="italic"
          textAnchor="middle"
        >
          Enter Pressure · Compliance to plot
        </text>
      ) : (
        <>
          {/* Curve */}
          <path
            d={curvePath.trim()}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {/* Peak marker */}
          <circle
            cx={xToPx(P)}
            cy={yToPx(C)}
            r="3"
            fill={color}
          />
          {/* Type / probe label — top left */}
          <text
            x={PAD.left + 4}
            y={PAD.top + 10}
            fill={color}
            fontFamily="Arial, sans-serif"
            fontSize="9"
            fontWeight="700"
            textAnchor="start"
          >
            {`Type ${jergerType || '—'} · ${probeHz} Hz`}
          </text>
          {/* Peak values — top right */}
          <text
            x={PAD.left + CW - 4}
            y={PAD.top + 10}
            fill={color}
            fontFamily="Arial, sans-serif"
            fontSize="9"
            fontWeight="700"
            textAnchor="end"
          >
            {`P=${P} · C=${C.toFixed(2)}`}
          </text>
        </>
      )}
    </svg>
  );
};

export default TympanogramCanvas;
